import * as R from "ramda";
import * as React from "react";
import { FiCircle } from "react-icons/fi";
import { SortEndHandler } from "react-sortable-hoc";
import { HintText, ItemLabel } from "../../../App/Common/Components/FormComponents";
import { getSortableHelperContainer } from "../../../utils";
import { courseAssessmentViews, courseUnitViews, fullCourseViews } from "../../../utils/courseViews";
import { AddInstructionPartWithDropZone } from "../../pages/CourseAuthor/CourseInstruction/Parts/AddInstructionPart";
import { Divider, Input, NarrowSelect, Select } from "../../pages/CourseAuthor/FormElements";
import { H1 } from "../../utils/Headings";
import { DefaultNames, ICourseContentSettings, QuestionDrawingPreference } from "../types";
import { hints, OrderPreferenceOptions, PassRequirementOptions } from "./Assessment.data";
import { Form, FormRow } from "./Assessment.styles";
import { QuestionOptions } from "./QuestionOptions.component";
import { SortablePartList } from "./SortablePartList";
import { IAssessmentProps, IAssessmentState, InputNames, PassRequirementValue } from "./types";

export function passRequirementOptionValueMapper({
  doPostAssessment,
  doPreAssessment,
}: ICourseContentSettings): PassRequirementValue {
  if (doPostAssessment && doPreAssessment) {
    return PassRequirementValue.Assessment;
  }

  if (doPostAssessment && !doPreAssessment) {
    return PassRequirementValue.AssessmentAndReview;
  }

  if (!doPostAssessment && doPreAssessment) {
    return PassRequirementValue.AssessmentOrReview;
  }

  if (!doPostAssessment && !doPreAssessment) {
    return PassRequirementValue.Review;
  }

  throw new Error("Invalid course content settings");
}

export function optionValuePassRequirementMapper(value: PassRequirementValue): ICourseContentSettings {
  return {
    [PassRequirementValue.AssessmentAndReview]: {
      doPostAssessment: true,
      doPreAssessment: false,
    } as ICourseContentSettings,
    [PassRequirementValue.AssessmentOrReview]: {
      doPostAssessment: false,
      doPreAssessment: true,
    } as ICourseContentSettings,
    [PassRequirementValue.Assessment]: {
      doPostAssessment: true,
      doPreAssessment: true,
    } as ICourseContentSettings,
    [PassRequirementValue.Review]: {
      doPostAssessment: false,
      doPreAssessment: false,
    } as ICourseContentSettings,
  }[value];
}

export class AssessmentComponent extends React.Component<IAssessmentProps, IAssessmentState> {
  private refObjects: {
    [InputNames.Accumulate]: React.RefObject<HTMLInputElement>;
    [InputNames.MistakesAllowed]: React.RefObject<HTMLSelectElement>;
    [InputNames.NumRequiredCorrect]: React.RefObject<HTMLSelectElement>;
    [InputNames.OrderPreference]: React.RefObject<HTMLSelectElement>;
    [InputNames.Randomize]: React.RefObject<HTMLInputElement>;
    [InputNames.RequiredAssessment]: React.RefObject<HTMLSelectElement>;
    [InputNames.ShowCorrect]: React.RefObject<HTMLInputElement>;
  };

  constructor(props: IAssessmentProps) {
    super(props);
    const test = fullCourseViews.assessment(props.course, props.unitPath);
    const settings = fullCourseViews.assessmentSettings(props.course, props.unitPath);

    if (R.isNil(test) || R.isNil(settings)) {
      throw new Error("Could not find assessment");
    }

    this.state = {
      formValues: {
        [InputNames.Accumulate]: test.accumulateAnswers,
        [InputNames.MistakesAllowed]: test.limitAllowedMistakes ? test.numAllowedMistakes : -1,
        [InputNames.NumRequiredCorrect]: courseAssessmentViews.requiredCorrect(test),
        [InputNames.OrderPreference]: test.questionDrawingPreference,
        [InputNames.Randomize]: test.isRandomized,
        [InputNames.RequiredAssessment]: passRequirementOptionValueMapper(settings),
        [InputNames.ShowCorrect]: settings.showCorrectAnswersAfterAssessment,
      },
      test,
    };

    this.refObjects = {
      [InputNames.Accumulate]: React.createRef(),
      [InputNames.MistakesAllowed]: React.createRef(),
      [InputNames.NumRequiredCorrect]: React.createRef(),
      [InputNames.OrderPreference]: React.createRef(),
      [InputNames.Randomize]: React.createRef(),
      [InputNames.RequiredAssessment]: React.createRef(),
      [InputNames.ShowCorrect]: React.createRef(),
    };
  }

  public setNumRequiredCorrect = () => {
    const { editOperations, unitPath: path } = this.props;
    const value = this.selectValue(InputNames.NumRequiredCorrect);

    if (R.isNil(value)) {
      throw new Error("Invalid value for numRequiredCorrect");
    }

    editOperations.modifyAssessmentNumCorrectRequired(path, parseInt(value, 10));
  };

  public setMistakesAllowed = () => {
    const { editOperations, unitPath } = this.props;
    const value = this.selectValue(InputNames.MistakesAllowed);

    if (R.isNil(value)) {
      throw new Error("Invalid value for mistakesAllowed");
    }

    const numValue = parseInt(value, 10);

    if (numValue >= 0) {
      editOperations.modifyAssessmentMistakesAllowed(unitPath, numValue);
    } else if (numValue === -1) {
      editOperations.modifyAssessmentLimitMistakes(unitPath, false);
    }
  };

  public setRandomize = () => {
    const { course, editOperations, unitPath } = this.props;
    const test = fullCourseViews.assessment(course, unitPath);

    if (R.isNil(test)) {
      throw new Error("Could not find course assessment");
    }

    editOperations.modifyAssessmentRandomized(unitPath, !test.isRandomized);
  };

  public setAccumulate = () => {
    const { course, editOperations, unitPath: path } = this.props;
    const test = fullCourseViews.assessment(course, path);

    if (R.isNil(test)) {
      throw new Error("Could not find course assessment");
    }

    editOperations.modifyAssessmentAccumulate(path, !test.accumulateAnswers);
  };

  public setDrawingPreference = () => {
    const { editOperations, unitPath: path } = this.props;
    const value = this.selectValue<QuestionDrawingPreference>(InputNames.OrderPreference);

    if (R.isNil(value)) {
      throw new Error("Invalid value for questionDrawingPreference");
    }

    editOperations.modifyAssessmentDrawingPreference(path, value);
  };

  public setPassRequirement = () => {
    const { editOperations, unitPath: path } = this.props;
    const value = this.selectValue<PassRequirementValue>(InputNames.RequiredAssessment);

    if (R.isNil(value)) {
      throw new Error("Invalid value for pass requirements");
    }

    editOperations.modifyAssessmentSettings(path, optionValuePassRequirementMapper(value));
  };

  public setShowCorrectAnswers = () => {
    const { course, editOperations, unitPath: path } = this.props;
    const settings = fullCourseViews.assessmentSettings(course, path);

    if (R.isNil(settings)) {
      throw new Error("Could not find course assessment settings");
    }

    editOperations.modifyAssessmentShowCorrect(path, !settings.showCorrectAnswersAfterAssessment);
  };

  public render() {
    const { course, currentLocale, unitPath, editOperations } = this.props;
    const unit = R.last(fullCourseViews.unitsInPath(course, unitPath));
    const test = fullCourseViews.assessment(course, unitPath);
    const settings = fullCourseViews.assessmentSettings(course, unitPath);

    if (R.isNil(test) || R.isNil(settings)) {
      throw new Error("Could not find course assessment");
    }

    const title = R.isNil(unit)
      ? fullCourseViews.localizedTitle(course, currentLocale) || DefaultNames.Course
      : courseUnitViews.localizedTitle(unit, currentLocale) || DefaultNames.Unit;
    const questions = courseAssessmentViews.questions(test) || [];
    const {
      accumulateHint,
      mistakesAllowedHint,
      numRequiredCorrectHint,
      preferenceHint,
      randomizeHint,
      requirementHint,
      showCorrectHint,
    } = hints;
    const nonDeletedQuestions = questions.filter(q => !q.deleted);
    return (
      <>
        <H1>Unit Assessment for {title}</H1>
        <Form>
          <ItemLabel htmlFor={InputNames.NumRequiredCorrect}>Correct Answers Required to Pass</ItemLabel>
          <FormRow>
            <NarrowSelect
              name={InputNames.NumRequiredCorrect}
              onChange={this.setNumRequiredCorrect}
              ref={this.refObjects[InputNames.NumRequiredCorrect]}
              value={this.state.formValues[InputNames.NumRequiredCorrect] as string}
            >
              <option value={0}>1</option>
              <QuestionOptions questions={questions} />
            </NarrowSelect>
            <FiCircle color="green" size={27.5} />
          </FormRow>
          <HintText>{numRequiredCorrectHint}</HintText>
          <Divider />
          <ItemLabel htmlFor={InputNames.MistakesAllowed}>Number of Mistakes to Fail</ItemLabel>
          <FormRow>
            <NarrowSelect
              name={InputNames.MistakesAllowed}
              onChange={this.setMistakesAllowed}
              ref={this.refObjects[InputNames.MistakesAllowed]}
              value={this.state.formValues[InputNames.MistakesAllowed] as string}
            >
              <option value={0}>1</option>
              <QuestionOptions questions={questions} />
              <option value={-1}>Unlimited</option>
            </NarrowSelect>
            <FiCircle color="red" size={27.5} />
          </FormRow>
          <HintText>{mistakesAllowedHint}</HintText>
          <Divider />
          <ItemLabel htmlFor={InputNames.Randomize}>
            <Input
              checked={test.isRandomized}
              name={InputNames.Randomize}
              onChange={this.setRandomize}
              ref={this.refObjects[InputNames.Randomize]}
              type="checkbox"
            />
            Randomize
          </ItemLabel>
          <HintText>{randomizeHint}</HintText>
          <Divider />
          <ItemLabel htmlFor={InputNames.Accumulate}>
            <Input
              checked={test.accumulateAnswers}
              name={InputNames.Accumulate}
              onChange={this.setAccumulate}
              ref={this.refObjects[InputNames.Accumulate]}
              type="checkbox"
            />
            Accumulate
          </ItemLabel>
          <HintText>{accumulateHint}</HintText>
          <Divider />
          <ItemLabel htmlFor={InputNames.OrderPreference}>Order Preference</ItemLabel>
          <Select
            name={InputNames.OrderPreference}
            onChange={this.setDrawingPreference}
            ref={this.refObjects[InputNames.OrderPreference]}
            value={this.state.formValues[InputNames.OrderPreference] as string}
          >
            {OrderPreferenceOptions.map(({ name, value }, i) => (
              <option key={i} value={value}>
                {name}
              </option>
            ))}
          </Select>
          <HintText>{preferenceHint}</HintText>
          <Divider />
          <ItemLabel htmlFor={InputNames.RequiredAssessment}>Requirement to Pass</ItemLabel>
          <Select
            name={InputNames.RequiredAssessment}
            onChange={this.setPassRequirement}
            ref={this.refObjects[InputNames.RequiredAssessment]}
            value={this.state.formValues[InputNames.RequiredAssessment] as string}
          >
            {PassRequirementOptions.map(({ name, value }, i) => (
              <option key={i} value={value}>
                {name}
              </option>
            ))}
          </Select>
          <HintText>{requirementHint}</HintText>
          <Divider />
          <ItemLabel htmlFor={InputNames.ShowCorrect}>
            <Input
              checked={settings.showCorrectAnswersAfterAssessment}
              name={InputNames.ShowCorrect}
              onChange={this.setShowCorrectAnswers}
              ref={this.refObjects[InputNames.ShowCorrect]}
              type="checkbox"
            />
            Show correct answers in review mode
          </ItemLabel>
          <HintText>{showCorrectHint}</HintText>
        </Form>
        <h1>{nonDeletedQuestions.length} Questions</h1>
        <SortablePartList
          assessment={test}
          axis="y"
          courseId={this.props.courseId}
          currentLocale={currentLocale}
          editOperations={editOperations}
          lockAxis="y"
          helperContainer={getSortableHelperContainer()}
          questionParts={questions}
          unitPath={unitPath}
          onSortEnd={this.partSortEnd}
          useDragHandle={true}
        />
        <AddInstructionPartWithDropZone
          dropPosition={undefined}
          dropZoneType="question"
          newInstructionType={null}
          editOperations={editOperations}
          path={unitPath}
          instructionSlide={test}
          addPart={editOperations.addPart(unitPath, test)}
          includeExplanation={false}
          includeGrids={false}
          includeImages={false}
          includeDocuments={false}
          displayOnStart={nonDeletedQuestions.length === 0}
        />
      </>
    );
  }

  private partSortEnd: SortEndHandler = ({ newIndex, oldIndex }) => {
    const from: number = oldIndex;
    const to: number = newIndex;

    if (from !== to) {
      this.props.sortPart({ from, to });
    }
  };

  private selectValue = <T extends string>(inputName: InputNames): Nullable<T> => {
    const selector = this.refObjects[inputName].current;

    if (!R.isNil(selector)) {
      this.setState({
        formValues: {
          ...this.state.formValues,
          [inputName]: selector.value,
        },
      });
      return (selector.value as T) || null;
    }

    return null;
  };
}
